import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import {
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  retry,
  skip,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import {
  IncidentEvent,
  IncidentEventNotificationInfoUpdate,
} from 'src/app/models/incident';
import { Usage } from 'src/app/models/subscription-plan';
import {
  AddIncidentEventAction,
  UpdateIncidentEventNotificationInfoAction,
} from 'src/app/store/incidents/incidents.actions';
import {
  EVENT_FIELDS,
  mapIncidentEvent,
} from 'src/app/store/incidents/incidents.service';
import {
  RealtimeReconnectedAction,
  SetRealtimeConnectionStateAction,
} from 'src/app/store/misc/misc.actions';
import { UsageUpdatedAction } from 'src/app/store/subscription/subscription.actions';
import { UserState } from 'src/app/store/user/user.state';

const EVENT_SUBSCRIPTION = gql`
  subscription Events($organizationUUID: ID!) {
    incidentEvents(organizationUUID: $organizationUUID) {
      incidentId
      event {
        ${EVENT_FIELDS}
        clientInstanceId
      }
    }
  }
`;

const NOTIFICATION_INFO_SUBSCRIPTION = gql`
  subscription IncidentEventNotificationInfo($organizationUUID: ID!) {
    incidentEventNotificationInfo(organizationUUID: $organizationUUID) {
      entityType
      entityId
      notificationInfo {
        state
        channels
        skipped {
          channel
          reason
        }
      }
    }
  }
`;

const USAGE_SUBSCRIPTION = gql`
  subscription Usage($organizationId: ID!) {
    usageEvents(organizationId: $organizationId) {
      featureName
      usage {
        unit
        value
      }
    }
  }
`;

interface IncidentSubscriptionEvent {
  incidentId: number;
  event: IncidentEvent;
}

interface UsageSubscriptionEvent {
  featureName: string;
  usage: Usage;
}

@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  private store = inject(Store);
  private apollo = inject(Apollo);
  private actions = inject(Actions);
  private destroyRef = inject(DestroyRef);

  private static readonly BASE_BACKOFF_MS = 500;
  private static readonly MAX_BACKOFF_MS = 10_000;

  constructor() {
    this.initSubscriptions();
    this.initReconnectResync();
  }

  /** One set of subscriptions per organization: switching orgs tears the
   *  previous org's streams down, so its events can't leak into the new one. */
  private initSubscriptions() {
    this.store
      .select(UserState.getCurrentOrganizationID)
      .pipe(
        distinctUntilChanged(),
        switchMap(orgId =>
          orgId
            ? merge(
                this.incidentEvents(orgId),
                this.notificationInfoEvents(orgId),
                this.usageEvents(orgId)
              )
            : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /** graphql-ws reports `connected` once per socket, so every `connected`
   *  after the first is a reconnect; anything published in between is lost. */
  private initReconnectResync() {
    this.actions
      .pipe(
        ofActionDispatched(SetRealtimeConnectionStateAction),
        filter(action => action.connectionState === 'connected'),
        skip(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.store.dispatch(new RealtimeReconnectedAction()));
  }

  private incidentEvents(orgId: string) {
    return this.withBackoff(
      'Incident',
      this.apollo.subscribe<{ incidentEvents: IncidentSubscriptionEvent }>({
        query: EVENT_SUBSCRIPTION,
        variables: { organizationUUID: orgId },
      })
    ).pipe(
      tap(data =>
        this.store.dispatch(
          new AddIncidentEventAction(
            data.incidentEvents.incidentId,
            mapIncidentEvent(data.incidentEvents.event)
          )
        )
      )
    );
  }

  private notificationInfoEvents(orgId: string) {
    return this.withBackoff(
      'Notification info',
      this.apollo.subscribe<{
        incidentEventNotificationInfo: IncidentEventNotificationInfoUpdate;
      }>({
        query: NOTIFICATION_INFO_SUBSCRIPTION,
        variables: { organizationUUID: orgId },
      })
    ).pipe(
      tap(data => {
        const update = data.incidentEventNotificationInfo;
        this.store.dispatch(
          new UpdateIncidentEventNotificationInfoAction(
            update.entityType,
            update.entityId,
            update.notificationInfo
          )
        );
      })
    );
  }

  private usageEvents(orgId: string) {
    return this.withBackoff(
      'Usage',
      this.apollo.subscribe<{ usageEvents: UsageSubscriptionEvent }>({
        query: USAGE_SUBSCRIPTION,
        variables: { organizationId: orgId },
      })
    ).pipe(
      tap(data =>
        this.store.dispatch(
          new UsageUpdatedAction(
            data.usageEvents.featureName,
            data.usageEvents.usage
          )
        )
      )
    );
  }

  /**
   * Unwraps a GraphQL subscription and resubscribes on error with
   * exponential backoff. Each stream owns its retry count (reset by the next
   * delivered event), so a persistently-failing stream can't have its retry
   * timing reset (or inflated) by another healthy one.
   */
  private withBackoff<T>(
    label: string,
    source: Observable<{ data?: T | null }>
  ): Observable<T> {
    return source.pipe(
      map(({ data }) => data),
      filter((data): data is T => !!data),
      retry({
        resetOnSuccess: true,
        delay: (_error, retryCount) => {
          const backoff = Math.min(
            RealtimeService.BASE_BACKOFF_MS * 2 ** retryCount,
            RealtimeService.MAX_BACKOFF_MS
          );
          console.warn(
            `[RealtimeService] ${label} subscription failed.. Retrying in ${backoff}ms`
          );
          return timer(backoff);
        },
      })
    );
  }
}
